const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface User {
  id: string;
  email: string;
  points: number;
  isSubscribed?: boolean;
}

interface AuthResponse {
  user: User;
  token: string;
}

interface CheckoutSessionResp {
  url: string;
}

interface RegisterData {
  email: string;
  password: string;
}

interface LoginData {
  email: string;
  password: string;
}

interface Toy {
  _id: string;
  name: string;
  description: string;
  points: number;
  images: string[];
  rating: number;
  category: string;
  stockQuantity: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface OrderItem {
  toyId: string;
  quantity: number;
}

interface OrderData {
  items: OrderItem[];
  shippingAddress?: string;
}

interface Order {
  _id: string;
  user: string;
  items: {
    toy: Toy;
    quantity: number;
    pointsPerItem: number;
    totalPoints: number;
  }[];
  totalPoints: number;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  orderDate: string;
  shippingAddress?: string;
  createdAt: string;
  updatedAt: string;
}

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("toddlar_token="))
      ?.split("=")[1];
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${decodeURIComponent(token)}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "API request failed");
    }

    return data;
  }

  async register(userData: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    return this.handleResponse<AuthResponse>(response);
  }

  async login(credentials: LoginData): Promise<ApiResponse<AuthResponse>> {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(credentials),
    });

    return this.handleResponse<AuthResponse>(response);
  }

  async getProfile(): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ user: User }>(response);
  }

  async createSubscriptionCheckout(): Promise<
    ApiResponse<CheckoutSessionResp>
  > {
    const response = await fetch(
      `${API_BASE_URL}/billing/create-checkout-session`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
      }
    );
    return this.handleResponse<CheckoutSessionResp>(response);
  }

  async updateProfile(
    userData: Partial<User>
  ): Promise<ApiResponse<{ user: User }>> {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      method: "PUT",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    });

    return this.handleResponse<{ user: User }>(response);
  }

  // Toy endpoints
  async getToys(params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    minPoints?: number;
    maxPoints?: number;
    inStock?: boolean;
  }): Promise<ApiResponse<{ toys: Toy[]; pagination: any }>> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_BASE_URL}/toys${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ toys: Toy[]; pagination: any }>(response);
  }

  async getToy(id: string): Promise<ApiResponse<{ toy: Toy }>> {
    const response = await fetch(`${API_BASE_URL}/toys/${id}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ toy: Toy }>(response);
  }

  async getCategories(): Promise<ApiResponse<{ categories: string[] }>> {
    const response = await fetch(`${API_BASE_URL}/toys/categories`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ categories: string[] }>(response);
  }

  // Order endpoints
  async createOrder(
    orderData: OrderData
  ): Promise<ApiResponse<{ order: Order }>> {
    const response = await fetch(`${API_BASE_URL}/orders`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(orderData),
    });

    return this.handleResponse<{ order: Order }>(response);
  }

  async getUserOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ orders: Order[]; pagination: any }>> {
    const searchParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }

    const url = `${API_BASE_URL}/orders/my-orders${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;

    const response = await fetch(url, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ orders: Order[]; pagination: any }>(response);
  }

  async getOrder(id: string): Promise<ApiResponse<{ order: Order }>> {
    const response = await fetch(`${API_BASE_URL}/orders/${id}`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ order: Order }>(response);
  }

  async cancelOrder(id: string): Promise<ApiResponse<{ order: Order }>> {
    const response = await fetch(`${API_BASE_URL}/orders/${id}/cancel`, {
      method: "PATCH",
      headers: this.getAuthHeaders(),
    });

    return this.handleResponse<{ order: Order }>(response);
  }
}

export const apiService = new ApiService();
export type {
  User,
  AuthResponse,
  RegisterData,
  LoginData,
  ApiResponse,
  Toy,
  OrderItem,
  OrderData,
  Order,
};
